import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Clock, Lock, MessageCircle, CreditCard, Mail, ShieldCheck, Plus } from "lucide-react";

const Wrap = ({ children, className = "" }) => (
  <div className={`max-w-[1180px] mx-auto px-6 ${className}`}>{children}</div>
);

export default function Landing() {
  const nav = useNavigate();
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((ents) => ents.forEach(e => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.1 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }} data-testid="landing-root">
      {/* Nav */}
      <header className="border-b-2" style={{ borderColor: "var(--ink)" }}>
        <Wrap className="flex justify-between items-center py-4">
          <Link to="/" className="text-2xl font-display font-black" data-testid="brand-logo">
            Ask<span style={{ color: "var(--kiwi)" }}>Tax</span>
          </Link>
          <nav className="flex items-center gap-6">
            <a href="#pricing" className="text-sm font-display font-bold uppercase tracking-wide hidden sm:inline">Pricing</a>
            <a href="#how" className="text-sm font-display font-bold uppercase tracking-wide hidden sm:inline">How it works</a>
            <Link to="/login" className="text-sm font-display font-bold uppercase tracking-wide" data-testid="nav-sign-in">Sign in</Link>
            <button onClick={() => nav("/register")} className="at-btn sm" data-testid="nav-get-started">Get started</button>
          </nav>
        </Wrap>
      </header>

      {/* HERO */}
      <section className="py-24">
        <Wrap>
          <span className="eyebrow">New Zealand tax, answered</span>
          <h1 className="h1-display mt-6" style={{ fontSize: "clamp(52px,9vw,120px)" }}>
            One tax<br />question.<br />
            <span style={{ color: "var(--kiwi)" }}>One clear</span><br />
            <span className="font-serif-i font-normal" style={{ textTransform: "none" }}>answer.</span>
          </h1>
          <div className="mt-10 max-w-2xl">
            <p className="font-serif text-xl leading-relaxed" style={{ color: "var(--ink-2)" }}>
              You don't need a whole appointment for one nagging tax question. Ask it here and an{" "}
              <b>IRD-registered NZ tax agent</b> sends you a straight, plain-English answer within one business day.
            </p>
            <div className="mt-8 flex items-center gap-6 flex-wrap">
              <button onClick={() => nav("/register")} className="at-btn" data-testid="hero-ask-btn">
                Ask your question <ArrowRight size={18} />
              </button>
              <span className="text-sm" style={{ color: "var(--muted)" }}>
                Answered by a real accountant. <b>Always a human.</b>
              </span>
            </div>
          </div>
        </Wrap>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 border-t-2" style={{ borderColor: "var(--ink)", background: "var(--cream-2)" }}>
        <Wrap>
          <div className="mb-14 reveal">
            <span className="eyebrow">Simple pricing</span>
            <h2 className="h1-display mt-3" style={{ fontSize: "clamp(40px,6vw,72px)" }}>
              One price.<br />No surprises.
            </h2>
          </div>
          <div className="grid md:grid-cols-[2fr_1.2fr] gap-8 reveal">
            {/* Single */}
            <div className="at-card p-10">
              <span className="at-tag" style={{ background: "var(--kiwi)", color: "var(--cream-2)", border: "1px solid var(--ink)" }}>The whole idea</span>
              <div className="mt-6 flex items-end gap-2">
                <span className="text-3xl font-display font-bold" style={{ color: "var(--muted-2)" }}>$</span>
                <span className="font-display font-black" style={{ fontSize: 96, lineHeight: 1 }}>67</span>
                <span className="font-serif-i text-lg mb-4" style={{ color: "var(--muted)" }}>a question</span>
              </div>
              <p className="mt-4 font-serif italic" style={{ color: "var(--ink-2)" }}>
                Flat fee, <b>GST included</b>. No account, no retainer, no surprise invoices.
              </p>
              <ul className="mt-6 space-y-3">
                {["Written by a registered NZ tax agent", "A clear answer within one business day", "Plain English, citing the IRD rules that apply"].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={18} style={{ color: "var(--kiwi)", marginTop: 3, flexShrink: 0 }} strokeWidth={2.6} />
                    <span className="text-base">{t}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => nav("/register")} className="at-btn mt-8" data-testid="pricing-single-btn">
                Ask your question <ArrowRight size={16} />
              </button>
            </div>
            {/* 3-pack */}
            <div className="at-card p-10" style={{ background: "var(--cream-3)" }}>
              <div className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>or</div>
              <span className="at-tag mt-3" style={{ background: "var(--amber)", color: "var(--ink)", border: "1px solid var(--ink)" }}>Save $74</span>
              <div className="mt-6 flex items-end gap-2">
                <span className="text-3xl font-display font-bold" style={{ color: "var(--muted-2)" }}>$</span>
                <span className="font-display font-black" style={{ fontSize: 80, lineHeight: 1 }}>127</span>
              </div>
              <p className="mt-2 font-serif italic text-lg" style={{ color: "var(--ink-2)" }}>
                for three questions — <b>$42.33 each</b>
              </p>
              <div className="mt-3 text-xs font-display font-bold uppercase tracking-wider" style={{ color: "var(--kiwi)" }}>No expiry · use any time</div>
              <ul className="mt-6 space-y-3">
                {["Three questions, whenever they come up", "Same agent, same one-day answers"].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={18} style={{ color: "var(--kiwi)", marginTop: 3, flexShrink: 0 }} strokeWidth={2.6} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => nav("/register")} className="at-btn amber mt-8" data-testid="pricing-pack-btn">
                Get 3 questions
              </button>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-8 text-sm" style={{ color: "var(--muted)" }}>
            <span className="flex items-center gap-2"><Lock size={16} /> Secure payment by Stripe</span>
            <span className="flex items-center gap-2"><Check size={16} /> GST included on both</span>
            <span className="flex items-center gap-2"><Clock size={16} /> Answered within 1 business day</span>
          </div>
        </Wrap>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 border-t-2" style={{ borderColor: "var(--ink)" }}>
        <Wrap>
          <div className="mb-14 reveal">
            <span className="eyebrow">How it works</span>
            <h2 className="h1-display mt-3" style={{ fontSize: "clamp(40px,6vw,72px)" }}>Ask. Pay. Done.</h2>
            <p className="mt-4 font-serif text-lg" style={{ color: "var(--ink-2)" }}>
              No back-and-forth to book a time. Three steps, one answer in your inbox.
            </p>
          </div>
          <div className="grid gap-6 reveal">
            {[
              { n: "01", h: "Ask your question", p: "Type your tax question and a bit of context. The more specific you are, the sharper the answer.", ic: <MessageCircle size={28} /> },
              { n: "02", h: "Pay $67 securely", p: "One flat fee through Stripe. No account to create, no subscription, no surprises.", ic: <CreditCard size={28} /> },
              { n: "03", h: "Get your answer", p: "A registered tax agent reviews it personally and emails you a clear answer within one business day.", ic: <Mail size={28} /> },
            ].map(s => (
              <div key={s.n} className="at-card p-8 flex items-center gap-8">
                <div className="font-display font-black text-4xl" style={{ color: "var(--kiwi)" }}>{s.n}</div>
                <div className="flex-1">
                  <h3 className="font-display font-black uppercase tracking-wide text-xl">{s.h}</h3>
                  <p className="mt-2 font-serif" style={{ color: "var(--ink-2)" }}>{s.p}</p>
                </div>
                <div style={{ color: "var(--ink-2)" }} className="hidden md:block">{s.ic}</div>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* WHO ANSWERS */}
      <section className="py-24 border-t-2" style={{ borderColor: "var(--ink)", background: "var(--ink)", color: "var(--cream-2)" }}>
        <Wrap>
          <div className="grid md:grid-cols-[220px_1fr] gap-10 reveal">
            <div className="rounded-full overflow-hidden" style={{ width: 180, height: 180, border: "2px solid var(--cream-3)", background: "#101b26" }}>
              <img
                src="/jiten-joshi.jpg"
                alt="Jiten Joshi — IRD-registered NZ tax agent"
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 22%", display: "block" }}
                data-testid="who-answers-photo"
              />
            </div>
            <div>
              <span className="eyebrow light">Who answers</span>
              <h2 className="h1-display mt-3" style={{ fontSize: 44 }}>Hi, I'm Jiten Joshi.</h2>
              <p className="mt-6 font-serif text-lg leading-relaxed" style={{ color: "var(--cream-3)" }}>
                I'm an <b>IRD-registered New Zealand tax agent</b> and I run my own accounting practice.
                I read and write every AskTax answer myself.
              </p>
              <p className="mt-4 font-serif text-lg leading-relaxed" style={{ color: "var(--cream-3)" }}>
                Most people don't need a full accountant for one question, but there was nowhere to just <em>ask</em>.
                So I built AskTax. When you send a question, <b>a real qualified professional stands behind the answer</b> — always current NZ tax rules.
              </p>
            </div>
          </div>
        </Wrap>
      </section>

      {/* FAQ */}
      <section className="py-24 border-t-2" style={{ borderColor: "var(--ink)" }}>
        <Wrap>
          <div className="mb-10 reveal">
            <span className="eyebrow">Good to know</span>
            <h2 className="h1-display mt-3" style={{ fontSize: "clamp(36px,5vw,56px)" }}>The fine print, in plain words</h2>
          </div>
          <div className="space-y-3 reveal">
            {[
              { q: "What counts as one question?", a: "One specific tax question on a single topic. If what you send is really two, we'll tell you before charging anything extra." },
              { q: "How fast will I hear back?", a: "Within one business day. Most answers land the same day." },
              { q: "Who actually answers?", a: "An IRD-registered New Zealand tax agent. A qualified accountant reads and writes every answer personally." },
              { q: "What if my question is too complex?", a: "We'll be honest and tell you. Your $67 is credited toward a full one-on-one engagement." },
              { q: "What's your refund policy?", a: "If we genuinely can't answer, we refund the $67. A correct answer is still the answer, even if it wasn't what you hoped." },
            ].map((f, i) => (
              <details key={i} className="at-card p-6 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-center font-display font-bold uppercase tracking-wide list-none">
                  {f.q}
                  <Plus size={18} className="group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-4 font-serif" style={{ color: "var(--ink-2)" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </Wrap>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 border-t-2" style={{ borderColor: "var(--ink)", background: "var(--kiwi)", color: "var(--cream-2)" }}>
        <Wrap className="text-center reveal">
          <span className="eyebrow light">Ready when you are</span>
          <h2 className="h1-display mt-3" style={{ fontSize: "clamp(48px,7vw,88px)" }}>
            Ask your <span className="font-serif-i font-normal" style={{ textTransform: "none" }}>question.</span>
          </h2>
          <p className="mt-6 font-serif text-lg max-w-2xl mx-auto" style={{ color: "var(--cream-3)" }}>
            Type it, pay $67, and a registered NZ tax agent will have a clear answer for you within one business day.
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <button onClick={() => nav("/register")} className="at-btn amber" data-testid="cta-register-btn">
              Create account & ask <ArrowRight size={16} />
            </button>
            <button onClick={() => nav("/login")} className="at-btn ink" data-testid="cta-login-btn">
              I already have an account
            </button>
          </div>
        </Wrap>
      </section>

      <footer className="py-8 border-t-2" style={{ borderColor: "var(--ink)", background: "var(--ink)", color: "var(--cream-3)" }}>
        <Wrap className="flex flex-wrap justify-between items-center gap-4 text-sm">
          <div className="font-display font-black text-lg">Ask<span style={{ color: "var(--kiwi-2)" }}>Tax</span></div>
          <div className="flex items-center gap-6 text-xs font-display font-bold uppercase tracking-widest">
            <Link to="/legal" className="hover:text-white" style={{ color: "var(--cream-3)" }} data-testid="footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white" style={{ color: "var(--cream-3)" }} data-testid="footer-privacy">Privacy Policy</Link>
          </div>
          <div>© 2026 AskTax · asktax.co.nz</div>
          <div>IRD-registered New Zealand tax agent</div>
        </Wrap>
      </footer>
    </div>
  );
}
